/* The transparent sq-ft ROM engine. Every coefficient is read from the active
 * rate schedule and surfaced in the UI — nothing hidden in cells.
 *
 * Lines that are derived from an UNVALIDATED engine assumption (production
 * rates, equipment ratios, disposal/mobilisation tiers) carry `assumption:true`
 * so the UI can flag them distinctly from live Schedule A/B/C rates. */

const round = (n, d = 2) => Math.round((Number(n) || 0) * 10 ** d) / 10 ** d;
const money = (n) => round(n, 2);

/* Symptom 1 (ROM large-loss logic fix, round 2) — markupPct inputs arrive as
 * either a fraction (0.20) or a whole percent (20), from both the manual
 * Section D form and the AI estimator (which sent "15" meaning 15% and got
 * billed as 1500%). Treat anything with |value| > 1 as a percent. */
const normalizePct = (v) => {
    const n = Number(v) || 0;
    return Math.abs(n) > 1 ? n / 100 : n;
};

/* PM-tier titles are no longer per-head crew rows — they roll into one
 * supervision line computed as a % of field labour (see supervision_pct). Any
 * of these found in a (legacy) composition template are filtered out so old
 * schedules get the fix without a DB migration. */
const PM_TIER_ROLES = [
    "Project Manager", "Sr. Project Manager", "Senior Project Manager",
    "Assistant Project Manager", "Asst. Project Manager", "Project Coordinator",
];
const PM_TIER_SET = new Set(PM_TIER_ROLES.map((r) => r.toLowerCase()));

/* Item 12 — residential jobs never bill a "Supervisor"-titled labour line;
 * confirmed hard invoicing rule (denied immediately if submitted on a real
 * claim) — technicians only. Commercial is unaffected and keeps these as
 * normal per-head crew rows. Backstop-matches any role containing the word
 * "supervisor" so a new/renamed rate-card title is covered automatically. */
const SUPERVISOR_TIER_ROLES = ["Restoration Supervisor", "Remediation Supervisor"];
const SUPERVISOR_TIER_SET = new Set(SUPERVISOR_TIER_ROLES.map((r) => r.toLowerCase()));
const isSupervisorRole = (role) => {
    const r = String(role || "").toLowerCase();
    return SUPERVISOR_TIER_SET.has(r) || r.includes("supervisor");
};

/* Fallback supervision blend (weights tie to 100%) for schedules predating the
 * supervision_composition coefficient. Derived from the Ebenezer timesheet. */
const DEFAULT_SUPERVISION_COMP = [
    { role: "Sr. Project Manager", weight: 0.64 },
    { role: "Project Manager", weight: 0.18 },
    { role: "Assistant Project Manager", weight: 0.10 },
    { role: "Project Coordinator", weight: 0.08 },
];

/* Large-loss economy of scale (Fix — June 2026). The flat hr/sqft model has no
 * production-rate improvement at volume, so a 14k ft² full-gut over-predicted
 * labour ~2x (PPL Restoring Waters: ROM $1.36M vs actual $717k). These marginal
 * bands taper hr/sqft on the sqft INSIDE each band so there's no cliff; small
 * jobs (≤3,000 ft²) keep the full rate. Overridable via coefficients.large_loss_scale. */
const DEFAULT_LARGE_LOSS_BANDS = [
    { max_sqft: 3000, factor: 1.0 },
    { max_sqft: 8000, factor: 0.82 },
    { max_sqft: 15000, factor: 0.68 },
    { max_sqft: null, factor: 0.58 },
];

/* Scope labour efficiency (June 2026 — PPL Restoring Waters calibration). A full
 * gut tears out wet material rather than restoring it in place, so the per-sqft
 * labour-hour load is lower than the steep full_gut scope multiplier implies.
 * Applied on top of the scope multiplier; overridable via coefficients. */
const DEFAULT_SCOPE_LABOUR_EFFICIENCY = { light: 1.0, moderate: 1.0, heavy: 0.9, full_gut: 0.77 };

/* Scope equipment factor — a gut needs far less in-place drying gear than a
 * water-mitigation job at full IICRC air-mover ratios. Scales preset quantities. */
const DEFAULT_EQUIP_SCOPE_FACTOR = { light: 1.0, moderate: 1.0, heavy: 0.7, full_gut: 0.2 };

/* Scope consumables factor — a full gut consumes far more materials / antimicrobial
 * / PPE / poly than an in-place restoration, so the flat consumables % is scaled up. */
const DEFAULT_CONSUMABLES_SCOPE_FACTOR = { light: 0.8, moderate: 1.0, heavy: 1.6, full_gut: 2.6 };

/* Disposal scope factor (room/voice pricing round, 2026-10) — a 2 ft flood cut
 * generates far less debris than a full gut. Only applied when ROOMS carry a
 * demo level (see `usingRoomScope` in computeROM) — a plain job with no rooms
 * entered keeps disposal exactly as it priced before this round. */
const DEFAULT_DISPOSAL_SCOPE_FACTOR = { light: 0.6, moderate: 1.0, heavy: 1.3, full_gut: 1.8 };

/* A room's own cut height / demo level stands in for "scope" when pricing that
 * room's share of labour/disposal/consumables — reuses the same scope-keyed
 * coefficients above instead of inventing a parallel set. Rooms with no level
 * set (empty string) fall back to the job's own overall `scope`. */
const DEMO_LEVEL_TO_SCOPE = { "2ft": "light", "4ft": "heavy", full_gut: "full_gut" };

/* Site conditions dictated on-site (voice walkthrough or manual entry) each add
 * a labour uplift %. Stacked additively so every condition is a separately
 * visible, editable assumption — never hidden inside a single blended number. */
export const SITE_CONDITION_TYPES = [
    { v: "long_carry", label: "Long carry / equipment haul distance" },
    { v: "shared_elevator", label: "Shared / service elevator only" },
    { v: "stairs_only", label: "Stairs only, no elevator" },
    { v: "occupied", label: "Occupied building during work" },
    { v: "restricted_hours", label: "Restricted working hours" },
    { v: "security_signin", label: "Daily security sign-in" },
    { v: "other", label: "Other (no price impact)" },
];
const SITE_CONDITION_LABEL = Object.fromEntries(SITE_CONDITION_TYPES.map((t) => [t.v, t.label]));
export const DEFAULT_SITE_CONDITION_UPLIFT = {
    long_carry: 0.05, shared_elevator: 0.08, stairs_only: 0.10,
    occupied: 0.06, restricted_hours: 0.12, security_signin: 0.02, other: 0,
};

/* Shared OT feasibility check (Fix 3) — used by both the generation and the
 * override paths. Flags a line whose implied sustained OT/day/person exceeds the
 * ceiling instead of silently pricing unbounded overtime. */
function otFeasibility(roleHours, headcount, days, hoursPerDay, ceiling) {
    const hc = Number(headcount) || 0;
    const d = Number(days) || 0;
    if (hc <= 0 || d <= 0) return { infeasible: false, ot_per_day_per_person: 0 };
    const stdCapacity = hc * d * hoursPerDay;
    const otHours = Math.max(0, (Number(roleHours) || 0) - stdCapacity);
    const perDayPerPerson = otHours / d / hc;
    return { infeasible: perDayPerPerson > ceiling, ot_per_day_per_person: round(perDayPerPerson, 2) };
}

/* Marginal (bracket) disposal cost — avoids a flat $/sqft step distortion on
 * large jobs. Each tier rate applies only to the sqft inside its band. */
function tieredDisposal(sqft, tiers) {
    if (!Array.isArray(tiers) || !tiers.length) return null;
    let prev = 0, total = 0;
    for (const t of tiers) {
        const cap = t.max_sqft == null ? Infinity : t.max_sqft;
        const portion = Math.max(0, Math.min(sqft, cap) - prev);
        total += portion * (Number(t.rate) || 0);
        if (sqft <= cap) break;
        prev = cap;
    }
    return total;
}

/* Tiered + linear mobilisation — replaces the binary $600/$1,500 cliff at the
 * 100-mile threshold with graduated bands and a per-mile scale past the top. */
function tieredMobilisation(miles, tiers) {
    if (!Array.isArray(tiers) || !tiers.length) return null;
    let prevMax = 0;
    for (const t of tiers) {
        if (t.max_miles == null || miles <= t.max_miles) {
            const over = t.per_mile_over ? Math.max(0, miles - prevMax) * t.per_mile_over : 0;
            return (Number(t.base) || 0) + over;
        }
        prevMax = t.max_miles;
    }
    const last = tiers[tiers.length - 1];
    return Number(last.base) || 0;
}

/* Blended labour-hours scale factor for large losses — marginal bands so the
 * sqft inside each band carries that band's factor (no cliff). Returns ≤1. */
function largeLossScale(sqft, cfg) {
    const bands = (cfg && Array.isArray(cfg.bands) && cfg.bands.length) ? cfg.bands : DEFAULT_LARGE_LOSS_BANDS;
    if (sqft <= 0) return 1;
    let prev = 0, weighted = 0;
    for (const b of bands) {
        const cap = b.max_sqft == null ? Infinity : b.max_sqft;
        const portion = Math.max(0, Math.min(sqft, cap) - prev);
        weighted += portion * (Number(b.factor) || 1);
        if (sqft <= cap) break;
        prev = cap;
    }
    return weighted / sqft;
}

/* Property Scale flag — single-family homes price differently to commercial /
 * large-loss work (crew/day floors, output framing, calibration bucket).
 *
 * Item 8g (ROM Large-Loss Logic Fix v4, round 3) — Residential mode is a JOB
 * SCALE decision, not a property-type label. Multifamily is commercial-scale
 * work even when small (real evidence: Ebenezer Tower, a Multifamily job,
 * invoiced a PM/Supervision line — only billed on commercial-scale jobs), so
 * it no longer auto-qualifies. Only "Single Family Residential" is even a
 * CANDIDATE; computeROM() below still has to confirm unitCount<=1 AND the
 * implied crew (computed once under commercial assumptions) is <=3 before it
 * actually switches scale — see `residentialCandidate` / `runScale()`. */
const RESIDENTIAL_PROPERTY_TYPES = new Set(["Single Family Residential"]);
export function scaleFromPropertyType(pt) {
    return RESIDENTIAL_PROPERTY_TYPES.has(pt) ? "residential" : "commercial";
}

export function computeROM(inputs, schedule, calibration = null) {
    const c = schedule?.coefficients || {};
    const cond = schedule?.conditions || {};
    const labRateCard = schedule?.schedule_a || [];
    const equipCard = schedule?.schedule_c || [];

    const sqft = Math.max(0, Number(inputs.sqft) || 0);
    const lossType = inputs.lossType || "water";
    // Item 8g — resolved further down (runScale), once the crew chain can test
    // the unitCount<=1 AND crew<=3 condition. propertyTypeScale is only the
    // candidacy signal (Single Family Residential), never the final scale.
    const propertyTypeScale = inputs.propertyType ? scaleFromPropertyType(inputs.propertyType) : (inputs.propertyScale || "commercial");
    const scope = inputs.scope || "moderate";
    const secondaryLoss = (inputs.secondaryLossType || "").toLowerCase();
    const hasSecondary = !!secondaryLoss && secondaryLoss !== lossType && secondaryLoss !== "other";
    const SECONDARY_UPLIFT = { water: 0.10, fire: 0.12, mould: 0.12, storm: 0.08, smoke: 0.10 };
    const secondaryMult = hasSecondary ? (1 + (SECONDARY_UPLIFT[secondaryLoss] || 0.08)) : 1.0;
    const stories = Math.max(1, Number(inputs.stories) || 1);
    const usePrime = !!inputs.primeArea;
    const isEmergency = !!inputs.emergency;
    const includeRecon = !!inputs.includeRecon;
    const consumablesPct = Number(inputs.consumablesPct ?? cond.consumables_default_pct ?? 0.095);
    const otHoursPerDay = Math.max(0, Number(inputs.otHoursPerDay) || 0);
    const hoursPerDay = Number(c.hours_per_day) || 8;
    const opMarkup = Number(cond.overhead_profit_markup) || 1.2;
    const perDiem = Number(cond.per_diem_per_day) || 60;
    const scheduleBand = Number(cond.rom_confidence_band) || 0.15;
    const smallToolsPct = Number(cond.small_tools_pct) || 0.03;
    const otMult = Number(cond.ot_multiplier) || 1.5;

    /* Fix 1 / Fix 3 inputs */
    const unitCount = Math.max(1, Math.floor(Number(inputs.unitCount) || 1));
    const maxParallelFronts = Math.max(1, Number(c.max_parallel_fronts) || 6);
    const otCeilingPerDay = Number(c.sustained_ot_ceiling_per_day) || 4;

    /* Crew/day floors by property scale (Residential mode). Commercial keeps the
     * 3-person / 3-day minimum; residential drops to 1/1 so a small home water job
     * doesn't ship an inflated commercial floor. Tunable on the rate schedule. */
    const crewFloorByScale = { residential: 1, commercial: 3, ...(c.crew_floor_by_scale || {}) };
    const daysFloorByScale = { residential: 1, commercial: 3, ...(c.days_floor_by_scale || {}) };

    const assumptions = [];

    /* ---- Room-by-room demo-level pricing (voice/room pricing round, 2026-10) ----
     * When rooms[] carry a cut height, each room's OWN effective scope (its
     * demoLevel, or the job's overall `scope` if unset) drives its share of
     * labour/disposal/consumables. Every formula these factors feed into is
     * linear in sqft, so a sqft-weighted blend of the per-room factors is
     * mathematically identical to pricing each room separately and summing —
     * just far simpler than re-running the whole crew/day chain per room.
     * With NO rooms entered, blendScopeFactor returns exactly what the engine
     * looked up before this round (byte-identical output). */
    const rooms = Array.isArray(inputs.rooms) ? inputs.rooms : [];
    const roomsWithSqft = rooms.filter((r) => (Number(r.sqft) || 0) > 0);
    const roomSqftTotal = roomsWithSqft.reduce((s, r) => s + Number(r.sqft), 0);
    const usingRoomScope = roomsWithSqft.length > 0 && roomSqftTotal > 0;
    function blendScopeFactor(map) {
        if (!usingRoomScope) return map[scope] ?? 1.0;
        let acc = 0;
        roomsWithSqft.forEach((r) => {
            const eff = DEMO_LEVEL_TO_SCOPE[r.demoLevel] || scope;
            acc += (Number(r.sqft) / roomSqftTotal) * (map[eff] ?? map[scope] ?? 1.0);
        });
        return acc;
    }
    if (usingRoomScope) {
        assumptions.push({
            key: "room_demo_pricing",
            label: "Room-by-room demo-level pricing",
            value: `${roomsWithSqft.length} room${roomsWithSqft.length === 1 ? "" : "s"} · blended scope ×${round(blendScopeFactor(c.scope_multiplier || {}), 2)}`,
            note: "Labour, disposal and consumables are priced per room by cut height (2 ft cut < 4 ft cut < full gut) and summed. Rooms with no demo level set use the job's overall scope. Production-rate factors are unvalidated — tune them on the Rate Schedule (Engine Coefficients).",
            unvalidated: true,
        });
    }

    /* ---- Site conditions → labour uplift (voice/room pricing round, 2026-10) ----
     * Each dictated/entered condition (long carry, shared elevator, stairs
     * only, occupied, restricted hours, security sign-in) stacks an additive
     * labour-hours uplift. Free-text / "other" conditions are informational
     * only and never affect price. */
    const siteConditionUpliftMap = { ...DEFAULT_SITE_CONDITION_UPLIFT, ...(c.site_condition_uplift_pct || {}) };
    const siteConditions = Array.isArray(inputs.siteConditions) ? inputs.siteConditions : [];
    const pricedConditions = siteConditions
        .map((sc) => (typeof sc === "string" ? { text: sc, type: "other" } : (sc || {})))
        .filter((sc) => sc.type && sc.type !== "other");
    let siteConditionUpliftPct = 0;
    pricedConditions.forEach((sc, i) => {
        const pct = (sc.upliftPct != null && sc.upliftPct !== "") ? normalizePct(sc.upliftPct) : (siteConditionUpliftMap[sc.type] ?? 0);
        siteConditionUpliftPct += pct;
        assumptions.push({
            key: `site_condition_${sc.id || i}`,
            label: `Site condition · ${SITE_CONDITION_LABEL[sc.type] || sc.type}`,
            value: `+${round(pct * 100, 1)}% labour`,
            note: `${sc.text ? `"${sc.text}" — ` : ""}unvalidated labour uplift, editable on this condition or on the Rate Schedule (Engine Coefficients).`,
            unvalidated: true,
        });
    });
    const siteConditionMult = 1 + siteConditionUpliftPct;

    /* ---- Labour hours ---- */
    const baseLHpSqft = (c.labour_hours_per_sqft || {})[lossType] ?? 0.2;
    const scopeMult = blendScopeFactor(c.scope_multiplier || {});
    if (scope === "light") {
        assumptions.push({
            key: "light_scope_multiplier",
            label: "Light-scope labour multiplier",
            value: `×${scopeMult}`,
            note: "The light-scope discount is an UNVALIDATED assumption — not yet confirmed against closed light-scope jobs. Treat the saving with caution.",
            unvalidated: true,
        });
    }
    let waterMult = 1.0;
    // In a full gut, water CATEGORY (contamination level) is largely subsumed —
    // the cost lives in disposal / consumables / PPE, not in extra labour hours.
    // So we don't stack Cat × full-gut on labour (Fix — overridable).
    const decoupleCategory = scope === "full_gut" && (c.full_gut_decouple_category !== false);
    if (lossType === "water") {
        const wcatRaw = (c.water_category_multiplier || {})[inputs.waterCategory || "cat1"] ?? 1.0;
        const wcat = decoupleCategory ? 1.0 : wcatRaw;
        const wcls = (c.water_class_multiplier || {})[inputs.waterClass || "class2"] ?? 1.0;
        waterMult = wcat * wcls;
    }
    const storiesMult = 1 + 0.06 * (stories - 1);
    const reconMult = includeRecon ? 1.45 : 1.0;
    // Large-loss economy of scale — taper hr/sqft as the job grows.
    const scaleFactor = largeLossScale(sqft, c.large_loss_scale);
    const scopeLabourEff = blendScopeFactor(c.scope_labour_efficiency || DEFAULT_SCOPE_LABOUR_EFFICIENCY);
    const totalLabourHours = sqft * baseLHpSqft * scopeMult * waterMult * storiesMult * reconMult * secondaryMult * scaleFactor * scopeLabourEff * siteConditionMult;
    if (decoupleCategory && lossType === "water" && (inputs.waterCategory || "cat1") !== "cat1") {
        assumptions.push({
            key: "fullgut_category_decoupled",
            label: `Full-gut · ${inputs.waterCategory} not stacked on labour`,
            value: "category cost → disposal / PPE",
            note: "In a full gut the structure is removed regardless of contamination category, so the category multiplier is not also applied to labour hours (it still drives disposal & consumables).",
        });
    }
    if (scaleFactor < 0.999) {
        assumptions.push({
            key: "large_loss_scale",
            label: "Large-loss economy of scale",
            value: `${Math.round((1 - scaleFactor) * 100)}% labour-hour taper @ ${Math.round(sqft).toLocaleString()} ft²`,
            note: "Production rates improve on large jobs — hr/ft² is tapered in marginal sq-ft bands above 3,000 ft². Assumption — editable on the rate schedule.",
        });
    }
    if (hasSecondary) {
        assumptions.push({
            key: "secondary_loss",
            label: `Secondary loss uplift · ${secondaryLoss}`,
            value: `+${Math.round((secondaryMult - 1) * 100)}% labour`,
            note: "Mixed-damage adjustment — added on top of the primary loss model.",
        });
    }
    assumptions.push({
        key: "labour_hours_per_sqft",
        label: `Labour hours / sq ft · ${lossType}`,
        value: `${baseLHpSqft} hr/ft²`,
        note: "Production-rate assumption — unvalidated against closed invoices.",
    });

    /* ---- Labour composition + crew/days chain, both scale-dependent (comp
     * drops Supervisor rows on residential, floors/caps differ by scale) — one
     * function so the item 8g gate below can run it once per candidate scale. */
    const rawComp = (c.labour_composition_templates || {})[lossType] || c.labour_composition || {};
    const usingTemplate = !!(c.labour_composition_templates || {})[lossType];
    const rateLookup = Object.fromEntries(labRateCard.map((r) => [r.role, r]));
    const targetDaysPer1000 = Number(c.default_target_days_per_1000sqft) || 1.2;
    const crewCapAbs = Number(c.max_crew_abs) || 24;
    const maxFieldDaysWarn = Number(c.max_field_days_warn) || 10;

    function runScale(scale) {
        // PM-tier roles never appear as per-head rows — they roll into the supervision
        // line. Item 12 — residential also drops any "Supervisor"-titled role outright
        // (hours drop out, not redistributed); commercial keeps them as normal rows.
        const comp = Object.fromEntries(
            Object.entries(rawComp).filter(([role]) => {
                if (PM_TIER_SET.has(String(role).toLowerCase())) return false;
                if (scale === "residential" && isSupervisorRole(role)) return false;
                return true;
            }),
        );
        const compShareTotal = Object.entries(comp).reduce((s, [role, sh]) => s + (rateLookup[role] ? (Number(sh) || 0) : 0), 0);
        const fieldHours = totalLabourHours * compShareTotal; // hours worked by the crew rows

        /* Crew + days: ONE chain, used by every branch (single unit, multi unit) ----
         *   fieldHours  ->  crew C (floored per work front, capped)  ->  field days D
         *   D = ceil(fieldHours / (C x hours per day))
         * Settings (rate schedule coefficients, approved provisional defaults):
         *   max_crew_per_1000sqft_by_scale (residential 3, commercial 6), max_crew_abs
         *   (24), max_field_days_warn (10). */
        const crewFloor = Math.max(1, Number(crewFloorByScale[scale]) || 3);
        const daysFloor = Math.max(1, Number(daysFloorByScale[scale]) || 3);
        const crewCapPer1000 = Number(({ residential: 3, commercial: 6, ...(c.max_crew_per_1000sqft_by_scale || {}) })[scale]) || 6;
        const crewCap = Math.max(crewFloor, Math.min(crewCapAbs, Math.ceil((sqft / 1000) * crewCapPer1000)));
        const fronts = unitCount > 1
            ? Math.max(1, Math.min(unitCount, maxParallelFronts, Math.floor(crewCap / crewFloor)))
            : 1;
        const minCrew = Math.min(crewCap, crewFloor * fronts);
        const prefDays = Math.max(daysFloor, Math.ceil((sqft / 1000) * targetDaysPer1000 * (scopeMult * 0.9)));
        let crewSize = Math.min(crewCap, Math.max(minCrew, Math.ceil(fieldHours / (prefDays * hoursPerDay))));
        let days = Math.max(1, Math.ceil(fieldHours / (crewSize * hoursPerDay)));
        if (days < daysFloor) {
            days = daysFloor;
            crewSize = Math.min(crewCap, Math.max(minCrew, Math.ceil(fieldHours / (days * hoursPerDay))));
        }

        /* Reconciliation — a density cap should never be the reason a job quietly
         * runs long. If finishing inside max_field_days_warn needs more crew than
         * the density cap allows, raise crew (up to the absolute cap) instead of
         * letting days blow out. Only if the absolute cap still can't finish in
         * time do days run past the guide — reported as one plain assumption
         * line below, never a "this estimate looks wrong" warning. */
        let crewRaisedForDays = false;
        if (days > maxFieldDaysWarn) {
            const neededCrew = Math.min(crewCapAbs, Math.ceil(fieldHours / (maxFieldDaysWarn * hoursPerDay)));
            if (neededCrew > crewSize) {
                crewSize = neededCrew;
                days = Math.max(1, Math.ceil(fieldHours / (crewSize * hoursPerDay)));
            }
            crewRaisedForDays = true;
        }

        /* Heads per role: largest remainder so the role heads ADD UP to the crew number.
         * A role that would round to zero heads folds its hours into the other roles. */
        const hoursShare = {};
        const headsByRole = {};
        {
            let active = Object.entries(comp)
                .filter(([role]) => rateLookup[role])
                .map(([role, sh]) => ({ role, share: (Number(sh) || 0) / (compShareTotal || 1) }));
            for (let guard = 0; guard < 50 && active.length; guard++) {
                const raw = active.map((a) => a.share * crewSize);
                const heads = raw.map(Math.floor);
                let rem = crewSize - heads.reduce((s, v) => s + v, 0);
                raw.map((v, i) => [v - Math.floor(v), i]).sort((a, b) => b[0] - a[0]).forEach(([, i]) => {
                    if (rem > 0) { heads[i] += 1; rem -= 1; }
                });
                const zeros = heads.map((h, i) => (h === 0 ? i : -1)).filter((i) => i >= 0);
                if (!zeros.length || active.length === 1) {
                    active.forEach((a, i) => { headsByRole[a.role] = heads[i]; hoursShare[a.role] = a.share * compShareTotal; });
                    break;
                }
                const drop = zeros.sort((i, j) => active[i].share - active[j].share)[0];
                active.splice(drop, 1);
                const t = active.reduce((s, a) => s + a.share, 0) || 1;
                active = active.map((a) => ({ ...a, share: a.share / t }));
            }
        }
        return { comp, compShareTotal, fieldHours, crewFloor, daysFloor, crewCapPer1000, crewCap, fronts, crewSize, days, hoursShare, headsByRole, crewRaisedForDays };
    }

    /* Item 8g — Residential mode is a JOB-SCALE decision: Single Family AND
     * unitCount<=1 AND the implied crew <=3. Run the chain once under
     * COMMERCIAL assumptions (the safe default) to test the crew<=3 condition
     * without circularity; only switch to residential — and re-run the chain
     * with its looser floors/caps — if the job actually qualifies. */
    const residentialCandidate = propertyTypeScale === "residential" && unitCount <= 1;
    let propertyScale = "commercial";
    let R = runScale("commercial");
    if (residentialCandidate && R.crewSize <= 3) {
        propertyScale = "residential";
        R = runScale("residential");
    }
    const { comp, compShareTotal, fieldHours, crewFloor, daysFloor, crewCapPer1000, crewCap, fronts, crewSize, days, hoursShare, headsByRole, crewRaisedForDays } = R;
    if (residentialCandidate) {
        assumptions.push({
            key: "residential_job_scale",
            label: "Residential job-scale check (item 8g)",
            value: propertyScale === "residential"
                ? `Residential — Single Family, ${unitCount} unit, crew ${crewSize} (<=3)`
                : `Commercial — Single Family but implied crew would exceed 3 at commercial floors`,
            note: "Residential mode is a job-scale decision (Single Family AND unitCount<=1 AND crew<=3), not just the property-type label.",
        });
    }
    const dynBand = calibration?.bands?.[`${lossType}__${propertyScale}`] ?? calibration?.bands?.[lossType];
    const band = (typeof dynBand === "number" && dynBand > 0) ? dynBand : scheduleBand;
    const bandSource = (typeof dynBand === "number" && dynBand > 0) ? "calibrated" : "schedule";
    assumptions.push({
        key: "crew_chain",
        label: "Crew and days",
        value: `${crewSize} people x ${days} field day${days === 1 ? "" : "s"} x ${hoursPerDay} h = ${crewSize * days * hoursPerDay} h capacity for ${round(fieldHours, 0)} h of crew work`,
        note: `One chain for every job. Density cap ${crewCap} (max ${crewCapPer1000} per 1,000 ft²); crew can rise above that, up to the absolute cap ${crewCapAbs}, to keep the job inside the ${maxFieldDaysWarn}-day guide. Floor ${crewFloor} per work front${fronts > 1 ? ` over ${fronts} fronts` : ""}. Caps are assumptions, editable on the rate schedule.`,
    });
    // The header "Labour hours" stat (Crew & timing) and the Labour table's
    // hours column must always agree. `fieldHours` (billed to per-head crew
    // rows) is compShareTotal x the full production-rate model — the gap is
    // PM-tier role time that never becomes a headcount row. Surface it
    // explicitly so the two numbers never look like they disagree silently.
    if (compShareTotal < 0.999) {
        assumptions.push({
            key: "labour_hours_reconciliation",
            label: "Labour hours · model vs billed to crew",
            value: `${round(totalLabourHours, 1)} modeled hrs -> ${round(fieldHours, 1)} hrs billed (${round(compShareTotal * 100, 1)}%)`,
            note: `The remaining ${round(totalLabourHours - fieldHours, 1)} hrs (${round((1 - compShareTotal) * 100, 1)}%) is PM-tier role time folded out of the per-head crew composition — it is never billed as a headcount row. Project Management / Supervision below is costed separately as its own % of field labour, not derived from this gap.`,
        });
    }
    if (crewRaisedForDays) {
        assumptions.push({
            key: "crew_days_reconciliation",
            label: "Crew and days reconciliation",
            value: `Crew set to ${crewSize} to finish in ${days} days`,
            note: days > maxFieldDaysWarn
                ? `Even at the absolute crew cap (${crewCapAbs}), this job needs ${days} field days.`
                : `The density cap alone would have run past the ${maxFieldDaysWarn}-day guide, so crew was raised toward the absolute cap (${crewCapAbs}) to keep the schedule inside it.`,
        });
    }
    if (fronts > 1) {
        assumptions.push({
            key: "max_parallel_fronts",
            label: "Parallel work fronts",
            value: `${fronts} concurrent of ${unitCount} units (cap ${maxParallelFronts})`,
            note: "Assumed crew-deployment limit — unvalidated against real multi-unit jobs. Crew stays under the overall cap.",
            unvalidated: true,
        });
    }
    assumptions.push({
        key: "target_days_per_1000sqft",
        label: "Preferred duration driver",
        value: `${targetDaysPer1000} days / 1,000 ft²`,
        note: "Used only to pick a preferred crew size before the cap. Field days always come from hours / (crew x 8).",
    });

    /* Fix 4 — full-gut + multi-unit is an explicitly untested combination. */
    if (scope === "full_gut" && unitCount > 1) {
        assumptions.push({
            key: "fullgut_multiunit",
            label: "Full-gut + multi-unit scope",
            value: "Production rate unvalidated against a closed job",
            note: "No closed multifamily full-gut job yet confirms this production rate — treat the hours and duration as a rough planning assumption, not a validated figure.",
            unvalidated: true,
        });
    }

    /* ---- Equipment — loss-type presets (carpet movers, large commercial dehus,
     * extractors, scrubbers, ozone/hydroxyl, HEPA vacs, negative air, etc.) ---- */
    const presets = (c.equipment_presets || {})[lossType] || [];
    const ratios = c.equipment_ratios || {};
    const equipScopeFactor = blendScopeFactor(c.equipment_scope_factor || DEFAULT_EQUIP_SCOPE_FACTOR);
    const equipPlan = [];
    const findEquip = (needle) =>
        equipCard.find((e) => e.item && e.item.toLowerCase().includes(String(needle).toLowerCase()));

    if (presets.length) {
        presets.forEach((p) => {
            const card = findEquip(p.match);
            if (!card) return;
            if (p.min_sqft && sqft < p.min_sqft) return;
            const per = Number(p.sqft_per_unit) || 1000;
            const qty = Math.max(Number(p.min_qty) || 1, Math.ceil((sqft * equipScopeFactor) / per));
            const d = p.days_cap ? Math.min(days, Number(p.days_cap)) : days;
            const cost = card.rate_per_day * qty * d * opMarkup;
            equipPlan.push({
                item: card.item, qty, days: d, rate: card.rate_per_day,
                subtotal: money(cost), assumption: true,
                basis: `~1 per ${per.toLocaleString()} ft²`,
            });
        });
        assumptions.push({
            key: "equipment_presets",
            label: `Equipment plan · ${lossType}`,
            value: `${equipPlan.length} preset items`,
            note: "Quantities derived from sq-ft ratios — assumption, not measured.",
        });
    } else {
        /* legacy fallback (thin model) if a schedule predates presets */
        const airMover = findEquip("air mover - axial") || findEquip("air mover");
        const lgr = findEquip("lgr") || findEquip("dehumidifier");
        const scrubber = findEquip("air scrubber - 1000") || findEquip("air scrubber");
        const extractor = findEquip("portable extractor") || findEquip("extractor");
        if (airMover && lossType === "water") {
            const qty = Math.max(2, Math.ceil(sqft / (ratios.air_mover_sqft_per_unit || 50)));
            const d = Math.min(days, 5);
            equipPlan.push({ item: airMover.item, qty, days: d, rate: airMover.rate_per_day, subtotal: money(airMover.rate_per_day * qty * d * opMarkup), assumption: true });
        }
        if (lgr && (lossType === "water" || lossType === "storm")) {
            const qty = Math.max(1, Math.ceil(sqft / (ratios.lgr_dehu_sqft_per_unit || 500)));
            const d = Math.min(days, 6);
            equipPlan.push({ item: lgr.item, qty, days: d, rate: lgr.rate_per_day, subtotal: money(lgr.rate_per_day * qty * d * opMarkup), assumption: true });
        }
        if (scrubber && (lossType === "mould" || lossType === "fire")) {
            const qty = Math.max(1, Math.ceil(sqft / (ratios.air_scrubber_sqft_per_unit || 1500)));
            equipPlan.push({ item: scrubber.item, qty, days, rate: scrubber.rate_per_day, subtotal: money(scrubber.rate_per_day * qty * days * opMarkup), assumption: true });
        }
        if (extractor && lossType === "water") {
            const qty = Math.max(1, Math.ceil(sqft / 4000));
            const d = Math.min(3, days);
            equipPlan.push({ item: extractor.item, qty, days: d, rate: extractor.rate_per_day, subtotal: money(extractor.rate_per_day * qty * d * opMarkup), assumption: true });
        }
    }

    // Crew transport van (all loss types) — skip if a vehicle preset already added one
    const van = findEquip("cargo / passenger van") || findEquip("cargo van") || findEquip("passenger van");
    if (van && !equipPlan.some((e) => e.item === van.item)) {
        const qty = Math.max(1, Math.ceil(crewSize / 8));
        equipPlan.push({
            item: van.item, qty, days, rate: van.rate_per_day,
            subtotal: money(van.rate_per_day * qty * days * opMarkup),
            assumption: true, basis: "1 per 8 crew",
        });
    }

    /* ---- Secondary loss equipment suggestions (mixed-damage) ---- */
    if (hasSecondary) {
        const ensure = (needles, opts = {}) => {
            const card = needles.map(findEquip).find(Boolean);
            if (!card) return;
            if (equipPlan.some((e) => e.item === card.item)) return;
            const per = opts.per || 800;
            const qty = Math.max(opts.minQty || 1, Math.ceil(sqft / per));
            const d = opts.daysCap ? Math.min(days, opts.daysCap) : days;
            equipPlan.push({
                item: card.item, qty, days: d, rate: card.rate_per_day,
                subtotal: money(card.rate_per_day * qty * d * opMarkup),
                assumption: true, secondary: true, basis: `secondary loss · ${secondaryLoss}`,
            });
        };
        if (secondaryLoss === "water") { ensure(["air mover"], { per: 50, minQty: 2, daysCap: 5 }); ensure(["lgr", "dehumidifier"], { per: 500, minQty: 1, daysCap: 6 }); }
        else if (secondaryLoss === "mould") { ensure(["air scrubber"], { per: 1500 }); ensure(["hepa"], { per: 1500 }); ensure(["negative air"], { per: 2000 }); }
        else if (secondaryLoss === "smoke") { ensure(["hydroxyl", "ozone", "air scrubber"], { per: 1500 }); }
        else if (secondaryLoss === "fire") { ensure(["air scrubber"], { per: 1500 }); ensure(["hepa"], { per: 1500 }); }
        else if (secondaryLoss === "storm") { ensure(["lgr", "dehumidifier"], { per: 500, minQty: 1, daysCap: 6 }); }
    }

    /* ---- Apply equipment overrides + extras (Section C is editable) ---- */
    const equipOverrides = inputs.equipment_overrides || {};
    const equipExtras = inputs.equipment_extras || [];
    const finalEquipLines = equipPlan.map((l) => {
        const ov = equipOverrides[l.item];
        if (ov && ov.removed) return null;
        if (!ov) return l;
        const qty = ov.qty !== undefined ? Number(ov.qty) : l.qty;
        const eDays = ov.days !== undefined ? Number(ov.days) : l.days;
        const rate = ov.rate !== undefined ? Number(ov.rate) : l.rate;
        const subtotal = money(qty * eDays * rate * opMarkup);
        return { ...l, qty, days: eDays, rate, subtotal, overridden: true };
    }).filter(Boolean);
    equipExtras.forEach((x, i) => {
        const qty = Number(x.qty) || 0;
        const eDays = Number(x.days) || 0;
        const rate = Number(x.rate) || 0;
        finalEquipLines.push({
            item: x.item || `Custom equipment ${i + 1}`,
            qty, days: eDays, rate,
            subtotal: money(qty * eDays * rate * opMarkup),
            custom: true,
        });
    });
    const equipmentTotal = finalEquipLines.reduce((s, l) => s + l.subtotal, 0);

    /* ---- Project plan (criteria-driven suggested phases; actual dates added in UI) ---- */
    const dryingEquip = equipPlan.some((e) => /dehumidif|air mover|lgr|extractor|desiccant/i.test(e.item || ""));
    const cleaningLoss = [lossType, secondaryLoss].some((t) => ["fire", "smoke", "mould"].includes(t));
    const mouldInvolved = [lossType, secondaryLoss].includes("mould");
    const heavyScope = scope === "heavy" || scope === "full_gut";
    const subCount = (inputs.subcontractors || []).filter((s) => (Number(s.qty) || 0) > 0 && (Number(s.rate) || 0) > 0).length;

    const planSpecs = [
        { key: "site-review", office: "open", phase: "Site Review / Initial Assessment", weight: 0.6, driver: "Standard first step" },
        { key: "scope-confirmation", office: "open", phase: "Scope Confirmation", weight: 0.5, driver: "Standard planning step" },
        equipPlan.length ? { key: "equipment-setup", phase: "Equipment Setup", weight: 0.6, driver: `Added due to equipment plan (${equipPlan.length} item${equipPlan.length === 1 ? "" : "s"})` } : null,
        (heavyScope || lossType === "fire" || includeRecon) ? { key: "mitigation-stripout", phase: "Mitigation / Strip-Out", weight: 1.4, driver: includeRecon ? "Added due to reconstruction scope" : `Added due to ${scope} scope` } : null,
        mouldInvolved ? { key: "mould-remediation", phase: "Mould Assessment & Remediation", weight: 1.2, driver: lossType === "mould" ? "Added due to mould loss" : "Added due to secondary mould loss" } : null,
        dryingEquip ? { key: "drying-monitoring", phase: "Drying / Monitoring", weight: 1.4, driver: "Added due to drying equipment (dehumidifiers / air movers)" } : null,
        cleaningLoss ? { key: "cleaning-treatment", phase: "Cleaning / Treatment", weight: 1.0, driver: "Added due to fire / smoke / mould cleaning" } : null,
        subCount ? { key: "specialist-subs", phase: "Specialist / Subcontractor Works", weight: 1.0, driver: `Added due to ${subCount} subcontractor line${subCount === 1 ? "" : "s"}` } : null,
        { key: "final-checks", office: "last", phase: "Final Checks", weight: 0.6, driver: "Standard QA step" },
        { key: "client-review", office: "after", phase: "Client / Insurer Review", weight: 0.5, driver: "Standard sign-off step" },
        { key: "close-out", office: "after", phase: "Close-Out", weight: 0.4, driver: "Standard demobilisation" },
    ].filter(Boolean);
    const fieldSpecs = planSpecs.filter((p) => !p.office);
    const fieldWeight = fieldSpecs.reduce((s, p) => s + p.weight, 0) || 1;
    let fieldCum = 0;
    const seededTimeline = planSpecs.map((spec) => {
        let start, phaseDays;
        if (!spec.office) {
            // Field phases are laid across the D field days. They may overlap when D is short.
            const s0 = fieldCum / fieldWeight;
            fieldCum += spec.weight;
            const s1 = fieldCum / fieldWeight;
            start = Math.min(days, Math.floor(s0 * days) + 1);
            phaseDays = Math.max(start, Math.round(s1 * days)) - start + 1;
        } else if (spec.office === "open") { start = 1; phaseDays = 1; }          // runs alongside day 1
        else if (spec.office === "last") { start = days; phaseDays = 1; }          // runs alongside the last field day
        else { start = days + 1; phaseDays = 1; }                                  // after the field work
        return { id: `phase-${spec.key}`, phase: spec.phase, start, days: phaseDays, field: !spec.office, tasks: [], driver: spec.driver, status: "not_started" };
    });
    const cursor = days;
    const tOv = inputs.timeline_overrides || {};
    const tExtras = inputs.timeline_extras || [];
    const finalTimeline = seededTimeline.map((p) => {
        const ov = tOv[p.id] || {};
        return {
            ...p,
            start: ov.start !== undefined ? Number(ov.start) : p.start,
            days: ov.days !== undefined ? Number(ov.days) : p.days,
            phase: ov.phase || p.phase,
            tasks: ov.tasks || p.tasks,
            status: ov.status || p.status,
            overridden: !!(ov.start !== undefined || ov.days !== undefined || ov.phase || ov.tasks || ov.status),
        };
    });
    tExtras.forEach((x, i) => finalTimeline.push({
        id: x.id || `extra-${i}`,
        phase: x.phase || `Custom phase ${i + 1}`,
        start: Number(x.start) || (cursor + 1),
        days: Number(x.days) || 1,
        tasks: x.tasks || [],
        status: x.status || "not_started",
        driver: x.driver || "Custom phase",
        custom: true,
    }));
    // The timeline is a DISPLAY of the plan. It never feeds crew, labour, per diem,
    // vans or overtime — those all use the field days above.
    const calendarDays = Math.max(
        days,
        ...finalTimeline.map((p) => (Number(p.start) || 1) + (Number(p.days) || 1) - 1),
    );

    const labourLines = [];
    let labourTotal = 0;
    Object.entries(comp).forEach(([role]) => {
        const card = rateLookup[role];
        if (!card) return;
        const share = hoursShare[role] ?? 0;
        const roleHeads = headsByRole[role] || 0;
        if (!roleHeads) return; // folded into the other roles
        const rate = usePrime ? card.prime : card.standard;
        const roleHours = totalLabourHours * share;
        // OT = hours demanded beyond standard crew capacity (heads × days × 8h),
        // with the estimator's otHoursPerDay acting as a forced-OT floor.
        const stdCapacity = roleHeads * days * hoursPerDay;
        const capacityOT = Math.max(0, roleHours - stdCapacity);
        const manualOT = otHoursPerDay * days * roleHeads;
        const otHours = Math.min(roleHours, Math.max(capacityOT, manualOT));
        const stHours = Math.max(0, roleHours - otHours);
        const stCost = stHours * rate;
        const otCost = otHours * rate * otMult;
        const otPremium = otHours * rate * (otMult - 1);
        const subtotal = stCost + otCost;
        labourTotal += subtotal;
        const feas = otFeasibility(roleHours, roleHeads, days, hoursPerDay, otCeilingPerDay);
        labourLines.push({
            role, headcount: roleHeads, hours: round(roleHours, 1),
            rate, ot_hours: round(otHours, 1), ot_premium: money(otPremium), field_role: !!card.field_role,
            subtotal: money(subtotal),
            infeasible: feas.infeasible, ot_per_day_per_person: feas.ot_per_day_per_person,
        });
    });

    // OT feasibility guard — if the implied crew is too small for the hours in
    // this duration, daily OT/person climbs past a sustainable ceiling. Surface it
    // as a flag (extend the schedule or add crew) rather than banking the OT cost.
    const infeasibleRoles = labourLines.filter((l) => l.infeasible).map((l) => l.role);
    if (infeasibleRoles.length) {
        assumptions.push({
            key: "ot_infeasible",
            label: "Sustained overtime beyond a feasible ceiling",
            value: infeasibleRoles.join(", "),
            note: "Implied crew is too small for the hours in this duration, forcing unsustainable daily overtime. Add crew or extend the schedule rather than relying on this overtime cost.",
            unvalidated: true,
        });
    }

    /* ---- Project Management / Supervision (Fix 2) — % of field labour, blended
     * across the PM-tier roles. Standalone line: no headcount, no OT split. ---- */
    const supervisionPct = (inputs.supervision_pct_override != null && inputs.supervision_pct_override !== "")
        ? Math.max(0, Number(inputs.supervision_pct_override))
        : Number((c.supervision_pct || {})[lossType] ?? 0.125);
    const supervisionComp = (Array.isArray(c.supervision_composition) && c.supervision_composition.length)
        ? c.supervision_composition
        : DEFAULT_SUPERVISION_COMP;
    let supervisionTotal = 0;
    if (propertyScale === "commercial" && supervisionPct > 0 && totalLabourHours > 0) {
        let wSum = 0, blended = 0;
        const compDetail = [];
        supervisionComp.forEach((sc) => {
            const card = rateLookup[sc.role];
            if (!card) return;
            const r = usePrime ? card.prime : card.standard;
            const w = Number(sc.weight) || 0;
            wSum += w; blended += w * r;
            compDetail.push({ role: sc.role, weight: w, rate: r });
        });
        const supRateFactor = Number(c.supervision_rate_factor ?? 0.63);
        const blendedRate = (wSum > 0 ? blended / wSum : 0) * supRateFactor;
        compDetail.forEach((d) => { d.weight = wSum > 0 ? round(d.weight / wSum, 4) : 0; }); // ties to 100%
        const supHours = totalLabourHours * supervisionPct;
        const supCost = supHours * blendedRate;
        supervisionTotal = money(supCost);
        labourTotal += supervisionTotal;
        labourLines.push({
            role: "Project Management / Supervision",
            supervision: true, pct: round(supervisionPct, 4),
            headcount: 0, hours: round(supHours, 1), rate: round(blendedRate, 2),
            ot_hours: 0, field_role: false, composition: compDetail,
            subtotal: supervisionTotal,
        });
        assumptions.push({
            key: "supervision_pct",
            label: `PM / Supervision · ${lossType}`,
            value: `${round(supervisionPct * 100, 1)}% of field labour`,
            note: "Supervision billed as a % of field labour (not a headcount row). Blended across the PM-tier — calibrated as Tier-B jobs close.",
        });
    }
    assumptions.push({
        key: "labour_composition",
        label: `Crew composition · ${lossType}`,
        value: usingTemplate ? "loss-type template" : "default mix (fallback)",
        note: "Role mix is a planning assumption — refine on Section A.",
    });

    // Small Tools 3% — flat add-on at the bottom of labour (Tech & below only).
    // Falls back to all labour if the schedule has no field_role flags (legacy).
    const fieldLabourBase = labourLines.reduce((s, l) => s + (l.field_role ? l.subtotal : 0), 0);
    const smallToolsBase = fieldLabourBase > 0 ? fieldLabourBase : labourTotal;
    const smallTools = smallToolsBase * smallToolsPct;
    labourTotal += smallTools;
    if (smallTools > 0) {
        labourLines.push({
            role: "Small Tools (3%)", headcount: 0, hours: 0, rate: 0,
            ot_hours: 0, subtotal: money(smallTools), auto: true,
        });
    }

    // Emergency service fee
    if (isEmergency) {
        const fee = usePrime ? (cond.emergency_service_fee_prime || 350) : (cond.emergency_service_fee_std || 200);
        labourTotal += fee;
        labourLines.push({
            role: "Emergency Service Fee", headcount: 0, hours: 0, rate: fee,
            ot_hours: 0, subtotal: money(fee), auto: true,
        });
    }

    /* ---- Apply labour overrides + extra rows from the user (Section A is editable) ---- */
    const overrides = inputs.labour_overrides || {};
    const extras = inputs.labour_extras || [];
    let overriddenTotal = 0;
    const finalLabourLines = labourLines.map((l) => {
        const ov = overrides[l.role];
        if (ov && ov.removed) return { ...l, subtotal: 0, removed: true };
        if (!ov) {
            overriddenTotal += l.subtotal;
            return l;
        }
        const headcount = ov.headcount !== undefined ? Number(ov.headcount) : l.headcount;
        const hours = ov.hours !== undefined ? Number(ov.hours) : l.hours;
        const rate = ov.rate !== undefined ? Number(ov.rate) : l.rate;
        let subtotal, otHours = l.ot_hours, otPremium = l.ot_premium || 0;
        if (l.auto) {
            // auto lines (Small Tools, Emergency Fee) carry a fixed dollar amount
            subtotal = ov.subtotal !== undefined ? Number(ov.subtotal) : l.subtotal;
        } else {
            // re-split the (overridden) total role hours into ST + OT so editing a
            // line never silently drops its overtime premium.
            const roleHours = hours || 0;
            const stdCapacity = (headcount || 0) * days * hoursPerDay;
            const capacityOT = Math.max(0, roleHours - stdCapacity);
            const manualOT = otHoursPerDay * days * (headcount || 0);
            otHours = Math.min(roleHours, Math.max(capacityOT, manualOT));
            const stHours = Math.max(0, roleHours - otHours);
            otPremium = money(otHours * (rate || 0) * (otMult - 1));
            subtotal = money(stHours * (rate || 0) + otHours * (rate || 0) * otMult);
        }
        overriddenTotal += subtotal;
        const feasOv = l.auto || l.supervision
            ? { infeasible: false, ot_per_day_per_person: 0 }
            : otFeasibility(hours || 0, headcount, days, hoursPerDay, otCeilingPerDay);
        return { ...l, headcount, hours, rate, ot_hours: round(otHours, 1), ot_premium: otPremium, subtotal, overridden: true, infeasible: feasOv.infeasible, ot_per_day_per_person: feasOv.ot_per_day_per_person };
    }).filter((l) => !l.removed);
    extras.forEach((x, i) => {
        const headcount = Number(x.headcount) || 0;
        const hours = Number(x.hours) || 0;
        const rate = Number(x.rate) || 0;
        const subtotal = money(headcount * hours * rate);
        overriddenTotal += subtotal;
        finalLabourLines.push({
            role: x.role || `Custom role ${i + 1}`,
            headcount, hours, rate, ot_hours: 0,
            subtotal, custom: true,
        });
    });
    labourTotal = overriddenTotal;

    /* ---- Consumables ---- */
    const consumablesScopeFactor = blendScopeFactor(c.consumables_scope_factor || DEFAULT_CONSUMABLES_SCOPE_FACTOR);
    const consumablesTotal = labourTotal * consumablesPct * consumablesScopeFactor;

    /* ---- Subcontractors (Section D — user-entered, now categorised) ---- */
    const SUB_DEFAULT_MARKUP = {
        labour: 0.20,      // subcontracted general labour +20%
        trade: 0.20,       // specialty / licensed trades +10/10
        rental_equip: 0.20, // rental equipment via sub +10/10
    };
    const subs = inputs.subcontractors || [];
    const finalSubLines = subs.map((s, i) => {
        const qty = Number(s.qty) || 0;
        const sDays = Number(s.days) || 0;
        const rate = Number(s.rate) || 0;
        const category = s.category || "trade";
        const mk = s.markupPct !== undefined ? normalizePct(s.markupPct) : (SUB_DEFAULT_MARKUP[category] ?? 0.20);
        const base = qty * rate * (sDays || 1);
        return {
            item: s.item || `Subcontractor ${i + 1}`,
            qty, days: sDays, rate, markupPct: mk, category,
            subtotal: money(base * (1 + mk)),
        };
    });
    const subsTotal = finalSubLines.reduce((s, l) => s + l.subtotal, 0);
    const subsByCategory = finalSubLines.reduce((acc, l) => {
        acc[l.category] = (acc[l.category] || 0) + l.subtotal;
        return acc;
    }, {});

    /* ---- Misc / Incidentals (Section E — editable, now tiered) ---- */
    const miscOv = inputs.misc_overrides || {};
    const miscExtras = inputs.misc_extras || [];
    const perDiemTotal = miscOv.per_diem !== undefined ? Number(miscOv.per_diem) : (inputs.farJob ? perDiem * crewSize * days : 0);

    const distThreshold = Number(cond.mobilisation_distance_threshold_miles) || 100;
    const miles = inputs.distance_miles != null
        ? Number(inputs.distance_miles)
        : (inputs.farJob ? distThreshold + 50 : 0);
    const mobTiers = c.mobilisation_tiers;
    const mobAuto = tieredMobilisation(miles, mobTiers);
    const mobilisation = miscOv.mobilisation !== undefined
        ? Number(miscOv.mobilisation)
        : (mobAuto != null ? round(mobAuto) : (inputs.farJob ? 1500 : 600));

    const permits = miscOv.permits !== undefined ? Number(miscOv.permits) : ((lossType === "fire" || includeRecon) ? 750 : 0);

    const dispTiers = (c.disposal_tiers || {})[lossType];
    const dispAuto = tieredDisposal(sqft, dispTiers);
    const disposalScopeFactor = usingRoomScope ? blendScopeFactor(c.disposal_scope_factor || DEFAULT_DISPOSAL_SCOPE_FACTOR) : 1.0;
    const disposalDefault = (dispAuto != null
        ? dispAuto
        : (lossType === "fire" ? sqft * 0.8 : lossType === "mould" ? sqft * 0.45 : sqft * 0.2)) * disposalScopeFactor;
    const disposal = miscOv.disposal !== undefined ? Number(miscOv.disposal) : disposalDefault;

    if (mobTiers) {
        assumptions.push({
            key: "mobilisation_tiers", label: "Mobilisation by distance",
            value: `${money(mobilisation)} @ ${miles} mi`,
            note: "Graduated distance bands — assumption, unvalidated.",
        });
    }
    if (dispTiers) {
        assumptions.push({
            key: "disposal_tiers", label: `Disposal · ${lossType}`,
            value: `${money(disposal)} (tiered $/ft²)`,
            note: "Marginal $/sq ft tiers — assumption, unvalidated.",
        });
    }

    const incidentalsRaw = perDiemTotal + mobilisation + permits + disposal;
    const miscExtrasTotal = miscExtras.reduce((s, x) => {
        const q = Number(x.qty) || 0;
        const r = Number(x.rate) || 0;
        const d = x.days != null ? Number(x.days) || 0 : 1;
        return s + q * r * (d || 1);
    }, 0);
    const incidentalsTotal = (incidentalsRaw + miscExtrasTotal) * opMarkup;
    /* Item 17-adjacent fix (incidentals breakdown) — the PDF/print view only ever
     * showed a single lump "Incidentals: $X" figure. Expose the same per-diem /
     * mobilisation / permits / disposal / custom-extra components already used
     * by the on-screen editable Misc table as a clean line-item array (with the
     * O&P markup applied per line, so the lines sum to incidentals_total). */
    const incidentalsLines = [
        { item: "Per diem (crew away from base)", subtotal: money(perDiemTotal * opMarkup) },
        { item: "Mobilisation", subtotal: money(mobilisation * opMarkup) },
        { item: "Permits", subtotal: money(permits * opMarkup) },
        { item: "Disposal / dumpster", subtotal: money(disposal * opMarkup) },
        ...miscExtras.map((x, i) => {
            const q = Number(x.qty) || 0;
            const r = Number(x.rate) || 0;
            const d = x.days != null ? Number(x.days) || 0 : 1;
            return { item: x.label || x.item || `Misc ${i + 1}`, subtotal: money(q * r * (d || 1) * opMarkup) };
        }),
    ].filter((l) => l.subtotal > 0);

    /* ---- Totals ---- */
    const grand = labourTotal + consumablesTotal + equipmentTotal + subsTotal + incidentalsTotal;
    /* ---- Invariants: crew, days and hours must reconcile on EVERY ROM ---- */
    const crewRows = labourLines.filter((l) => (Number(l.headcount) || 0) > 0);
    const headSum = crewRows.reduce((s, l) => s + l.headcount, 0);
    const rowHours = crewRows.reduce((s, l) => s + l.hours, 0);
    const capacityHours = crewSize * days * hoursPerDay;
    const otHoursTotal = crewRows.reduce((s, l) => s + (Number(l.ot_hours) || 0), 0);
    const invChecks = [
        { key: "heads_equal_crew", ok: headSum === crewSize, detail: `${headSum} heads vs crew ${crewSize}` },
        { key: "hours_reconcile", ok: capacityHours + otHoursTotal + 1 >= rowHours * 0.95, detail: `${round(capacityHours, 0)} h capacity + ${round(otHoursTotal, 0)} h OT vs ${round(rowHours, 0)} h` },
        { key: "crew_within_cap", ok: crewSize <= crewCapAbs, detail: `crew ${crewSize} vs absolute cap ${crewCapAbs}` },
        { key: "per_diem_basis", ok: true, detail: `per diem uses ${crewSize} people x ${days} days` },
    ];
    const invariants = { ok: invChecks.every((x) => x.ok), checks: invChecks };
    if (!invariants.ok) {
        assumptions.push({
            key: "invariant_failed",
            label: "Crew, days and hours do not reconcile",
            value: invChecks.filter((x) => !x.ok).map((x) => x.detail).join("; "),
            note: "Internal consistency check failed. Do not send this ROM until it is reviewed.",
            unvalidated: true,
        });
    }
    return {
        grand_total: money(grand),
        band_low: money(grand * (1 - band)),
        band_high: money(grand * (1 + band)),
        labour_total: money(labourTotal),
        consumables_total: money(consumablesTotal),
        equipment_total: money(equipmentTotal),
        subs_total: money(subsTotal),
        subs_by_category: subsByCategory,
        incidentals_total: money(incidentalsTotal),
        incidentals_lines: incidentalsLines,
        supervision_total: money(supervisionTotal),
        field_labour_total: money(labourTotal - supervisionTotal),
        unit_count: unitCount,
        field_hours_total: round(fieldHours, 1),
        labour_lines: finalLabourLines,
        equipment_lines: finalEquipLines,
        subcontractor_lines: finalSubLines,
        timeline: finalTimeline,
        days,                       // FIELD days: the number used for crew, equipment, vans, per diem and labour
        timeline_days: calendarDays, // display only: length of the project plan shown to the client
        field_days: days,
        crew_cap: crewCap,
        invariants,
        // Item 2 fix (regression bug batch) — the header "Implied crew" stat used
        // to be a separately-rounded aggregate (totalLabourHours ÷ calendarDays×8h)
        // that could disagree with the SUM of each labour role's own headcount
        // column (every role floors to a minimum of 1 head, so with several roles
        // that floor alone can push the row-sum above/below the aggregate). Report
        // the actual sum of per-role headcounts so the same number a client reads
        // in the Labour table also matches the header stat on the same PDF page.
        // (Supervision's headcount is always 0, so it never inflates this sum.)
        crew_size: crewSize,
        secondary_loss: secondaryLoss || null,
        property_scale: propertyScale,
        confidence_pct: round(band * 100, 1),
        band_source: bandSource,
        assumptions,
        rate_basis: usePrime ? "prime" : "standard",
        breakdown_inputs: {
            base_lh_per_sqft: baseLHpSqft, scope_mult: scopeMult, water_mult: waterMult,
            stories_mult: storiesMult, recon_mult: reconMult, scale_factor: round(scaleFactor, 4),
            category_decoupled: decoupleCategory,
            total_labour_hours: round(totalLabourHours, 1),
            per_diem_total: money(perDiemTotal), mobilisation, permits, disposal: money(disposal),
            distance_miles: miles,
            op_markup: opMarkup, consumables_pct: consumablesPct, consumables_scope_factor: round(consumablesScopeFactor, 4),
            misc_extras_total: money(miscExtrasTotal),
            using_room_scope: usingRoomScope, room_count: roomsWithSqft.length,
            site_condition_uplift_pct: round(siteConditionUpliftPct, 4),
        },
    };
}

export const formatCurrency = (n) =>
    Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const formatCurrencyExact = (n) =>
    Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/* Sub category labels shared between Calculator + Data Gaps. */
export const SUB_CATEGORIES = [
    { v: "trade", label: "Trade Sub" },
    { v: "labour", label: "Labour Sub" },
    { v: "rental_equip", label: "Rental Equipment via Sub" },
];
